import { AfterViewInit, Component, ComponentRef, OnDestroy, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { Subscription } from 'rxjs';

import { ThfComponentInjectorService, ThfModalAction, ThfModalComponent } from '@totvs/thf-ui';

import { browserLanguage, isExternalLink, isTypeof, thfLocaleDefault } from '../../utils/util';

import { ThfModalPasswordRecoveryComponent } from '../thf-modal-password-recovery/thf-modal-password-recovery.component';
import { ThfModalPasswordRecoveryType } from '../thf-modal-password-recovery/enums/thf-modal-password-recovery-type.enum';
import { ThfPageChangePassword } from './interfaces/thf-page-change-password.interface';
import { ThfPageChangePasswordBaseComponent } from './thf-page-change-password-base.component';
import { thfPageChangePasswordLiterals } from './literals/i18n/thf-page-change-password-literals';
import { ThfPageChangePasswordRecovery } from './interfaces/thf-page-change-password-recovery.interface';
import { ThfPageChangePasswordRequirement } from './interfaces/thf-page-change-password-requirement.interface';
import { ThfPageChangePasswordService } from './thf-page-change-password.service';

/**
 * @docsExtends ThfPageChangePasswordBaseComponent
 *
 * @example
 *
 * <example name="thf-page-change-password-basic" title="Totvs Page Change Password Basic">
 *  <file name="sample-thf-page-change-password-basic/sample-thf-page-change-password-basic.component.html"> </file>
 *  <file name="sample-thf-page-change-password-basic/sample-thf-page-change-password-basic.component.ts"> </file>
 * </example>
 *
 * <example name="thf-page-change-password-labs" title="Totvs Page Change Password Labs">
 *  <file name="sample-thf-page-change-password-labs/sample-thf-page-change-password-labs.component.html"> </file>
 *  <file name="sample-thf-page-change-password-labs/sample-thf-page-change-password-labs.component.ts"> </file>
 * </example>
 *
 * <example name="thf-page-change-password-modify" title="Totvs Page Change Password Modify">
 *  <file name="sample-thf-page-change-password-modify/sample-thf-page-change-password-modify.component.html"> </file>
 *  <file name="sample-thf-page-change-password-modify/sample-thf-page-change-password-modify.component.ts"> </file>
 * </example>
 *
 * <example name="thf-page-change-password-create" title="Totvs Page Change Password Create">
 *  <file name="sample-thf-page-change-password-create/sample-thf-page-change-password-create.component.html"> </file>
 *  <file name="sample-thf-page-change-password-create/sample-thf-page-change-password-create.component.ts"> </file>
 * </example>
 *
 * <example name="thf-page-change-password-request" title="Totvs Page Change Password Request">
 *  <file name="sample-thf-page-change-password-request/sample-thf-page-change-password-request.component.html"> </file>
 *  <file name="sample-thf-page-change-password-request/sample-thf-page-change-password-request.component.ts"> </file>
 * </example>
 */
@Component({
  selector: 'thf-page-change-password',
  templateUrl: './thf-page-change-password.component.html'
})

export class ThfPageChangePasswordComponent extends ThfPageChangePasswordBaseComponent implements AfterViewInit, OnDestroy, OnInit {

  private newPasswordSubscription: Subscription;

  readonly literals: { backButton: string, confirmPassword: string, createNewPassword: string, createNewPasswordPhrase: string,
    currentPassword: string, enterSystemButton: string, forgotPassword: string, newPassword: string, passwordSuccessfullyCreated: string,
    passwordSuccessfullyUpdated: string, safetyTips: string, safetyTipsPhrase: string, safetyTipsFirst: string, safetyTipsSecond: string,
    safetyTipsThird: string, saveButton: string } =
    {
      ...thfPageChangePasswordLiterals[thfLocaleDefault],
      ...thfPageChangePasswordLiterals[browserLanguage()],
    };

  private componentRef: ComponentRef<any> = null;

  modalAction: ThfModalAction = {
    action: this.navigateTo.bind(this, this.urlHome),
    label: this.literals.enterSystemButton
  };

  parentRef: any;

  @ViewChild(ThfModalComponent, { static: true }) modal: ThfModalComponent;
  @ViewChild('pageChangePassword', { read: ViewContainerRef, static: true }) pageChangePassword: ViewContainerRef;
  @ViewChild('passwordForm', { read: NgForm, static: true }) passwordForm: NgForm;

  constructor(
    private activatedRoute: ActivatedRoute,
    private route: ActivatedRoute,
    private router: Router,
    private service: ThfPageChangePasswordService,
    private thfComponentInjector: ThfComponentInjectorService,
    viewRef: ViewContainerRef) {
      super();
      this.parentRef = viewRef['_view']['component'];
  }

  ngAfterViewInit() {
    if (this.urlNewPassword) {
      this.subscribeToTokenParameter();
    }
  }

  ngOnDestroy() {
    if (this.newPasswordSubscription) {
      this.newPasswordSubscription.unsubscribe();
    }
  }

  ngOnInit() {
    this.checkingForRouteMetadata(this.activatedRoute.snapshot.data);
  }

  navigateTo(url: string) {
    isExternalLink(url) ?  window.open(url) : this.router.navigate([url || '/']);
  }

  onForgotPasswordClick(recovery): void {
    if (isTypeof(recovery, 'function')) {
      recovery();
    } else {
      this.createModalPasswordRecoveryComponent(recovery);
    }
  }

  onLoginSubmit(): void {

    const form = this.getLoginForm();

    if (this.urlNewPassword) {
      this.postUrlNewPassword(form);
    } else if (this.submit && this.submit.observers.length) {
      this.emitSubmit(form);
    }
  }

  /**
   * Abre uma modal de confirmaÃ§Ã£o com texto, imagem e botÃ£o que redireciona para o link definido na propriedade `t-url-home`
   */
  openConfirmation() {
    this.modal.open();
  }

  validatePassword() {
    const controls =  this.passwordForm.form.controls;
    const controlConfirmPassword = controls['confirmPassword'];
    const controlNewPassword = controls['newPassword'];

    if (!this.newPassword) {
      this.setFormErrors({'required': true}, [controlNewPassword]);
    } else if (!this.confirmPassword) {
      this.setFormErrors({'required': true}, [controlConfirmPassword]);
    } else if (this.newPassword && this.confirmPassword && this.newPassword !== this.confirmPassword) {
      this.setFormErrors({'equalPassword': true}, [controlNewPassword, controlConfirmPassword]);
    }  else {
      this.setFormErrors(null, [controlConfirmPassword, controlNewPassword]);
    }

    if (this.requirements.length && this.requirements.find(requirement =>  this.validateRequirement(requirement) === false)) {
      this.setFormErrors({'requirement': true}, [controlNewPassword]);
    }
  }

  validateRequirement(requirement: ThfPageChangePasswordRequirement) {
    return typeof requirement.status === 'function' ? requirement.status.call(this.parentRef, this.newPassword) : requirement.status;
  }

  private checkingForMetadataProperty(object, property) {
    if (Object.prototype.hasOwnProperty.call(object, property)) {
      return object[property];
    }
  }

  private checkingForRouteMetadata(data) {
    if (Object.keys(data).length !== 0) {
      this.urlNewPassword = this.checkingForMetadataProperty(data, 'serviceApi') || this.urlNewPassword;
      this.recovery = this.checkingForMetadataProperty(data, 'recovery') || this.recovery;
      this.hideCurrentPassword = this.checkingForMetadataProperty(data, 'hideCurrentPassword') || this.hideCurrentPassword;
    }
  }

  private createModalPasswordRecoveryComponent(recovery: ThfPageChangePasswordRecovery) {
    if (this.componentRef) {
      this.thfComponentInjector.destroyComponentInApplication(this.componentRef);
    }

    this.componentRef = this.thfComponentInjector.createComponentInApplication(ThfModalPasswordRecoveryComponent);
    this.componentRef.instance.recovery = recovery.url;
    this.componentRef.instance.contactEmail = recovery.contactMail;
    this.componentRef.instance.phoneMask = recovery.phoneMask;
    this.componentRef.instance.type = recovery.type || ThfModalPasswordRecoveryType.Email;
    this.componentRef.changeDetectorRef.detectChanges();
    setTimeout(() => {
      this.componentRef.instance.open();
    });
  }

  private emitSubmit(form: ThfPageChangePassword) {
    this.submit.emit(form);
  }

  private getLoginForm(): ThfPageChangePassword {
    return {
      currentPassword: this.currentPassword,
      newPassword: this.newPassword
    };
  }

  private postUrlNewPassword(form: ThfPageChangePassword) {

    form['token'] = this.token;

    this.service.post(this.urlNewPassword, form).subscribe(response => {
      if (response.status === 204) {
        this.openConfirmation();
      }
    });
  }

  private setFormErrors(error: any, controls?: Array<any>) {
    controls.forEach(control => {
      control.setErrors(error);
    });
  }

  private subscribeToTokenParameter() {
    this.route.queryParams.subscribe(params => {
      const token = params['token'];

      if (token) {
        this.token = token;
      }
    });
  }

}
